import React, { useState } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  Divider,
  Alert,
  IconButton,
  InputAdornment,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Google as GoogleIcon,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useAnalytics } from "../../hooks/useAnalytics";
import { colors } from "../../theme";
import { brandFilledButtonSx, brandTextAccentSx } from "../../utils/brandAssets";
import { buildAuthLink, getGoogleAuthUrl } from "./authHelpers";

const AuthCredentialsForm = ({ mode, searchParams, onSuccess }) => {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const { login, register } = useAuth();
  const { trackLogin, trackSignup } = useAnalytics();

  const alternateHref = buildAuthLink(
    mode === "login" ? "/signup" : "/login",
    searchParams
  );

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError("");
  };

  const validateForm = () => {
    if (mode === "register") {
      if (!formData.username.trim()) {
        setError("Username is required");
        return false;
      }
      if (formData.username.length < 3) {
        setError("Username must be at least 3 characters");
        return false;
      }
      if (formData.password !== formData.confirmPassword) {
        setError("Passwords do not match");
        return false;
      }
      if (formData.password.length < 6) {
        setError("Password must be at least 6 characters");
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!validateForm()) return;

    setLoading(true);

    try {
      let result;
      if (mode === "login") {
        result = await login(formData.email, formData.password);
      } else {
        result = await register(
          formData.username,
          formData.email,
          formData.password
        );
      }

      if (result.success) {
        if (mode === "login") {
          trackLogin("email", "auth_page", true);
        } else {
          trackSignup("email", true);
        }
        onSuccess();
      } else {
        setError(result.error);
      }
    } catch (submitError) {
      setError("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    trackLogin("google", "auth_page");
    window.location.href = getGoogleAuthUrl();
  };

  const formTitle =
    mode === "login" ? "Sign in to Stats.fan" : "Create your account";

  return (
    <>
      <Typography
        variant="h5"
        component="h1"
        sx={{
          fontWeight: 700,
          letterSpacing: "-0.02em",
          mb: 3,
        }}
      >
        {formTitle}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        variant="outlined"
        fullWidth
        startIcon={<GoogleIcon />}
        onClick={handleGoogleLogin}
        sx={{
          mb: 2,
          py: 1.25,
          borderColor: colors.border,
          color: colors.textPrimary,
          "&:hover": {
            borderColor: "rgba(255, 255, 255, 0.15)",
            bgcolor: "rgba(255, 255, 255, 0.04)",
          },
        }}
      >
        Continue with Google
      </Button>

      <Divider sx={{ my: 2 }}>
        <Typography variant="body2" color="text.secondary">
          or
        </Typography>
      </Divider>

      <Box component="form" onSubmit={handleSubmit}>
        {mode === "register" && (
          <TextField
            fullWidth
            label="Username"
            name="username"
            value={formData.username}
            onChange={handleInputChange}
            margin="normal"
            required
            autoFocus
          />
        )}

        <TextField
          fullWidth
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
          margin="normal"
          required
          autoFocus={mode === "login"}
        />

        <TextField
          fullWidth
          label="Password"
          name="password"
          type={showPassword ? "text" : "password"}
          value={formData.password}
          onChange={handleInputChange}
          margin="normal"
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  aria-label={
                    showPassword ? "Hide password" : "Show password"
                  }
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {mode === "register" && (
          <TextField
            fullWidth
            label="Confirm Password"
            name="confirmPassword"
            type={showConfirmPassword ? "text" : "password"}
            value={formData.confirmPassword}
            onChange={handleInputChange}
            margin="normal"
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() =>
                      setShowConfirmPassword(!showConfirmPassword)
                    }
                    edge="end"
                    aria-label={
                      showConfirmPassword
                        ? "Hide confirm password"
                        : "Show confirm password"
                    }
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={loading}
          sx={{
            ...brandFilledButtonSx,
            mt: 2,
            py: 1.35,
            fontWeight: 600,
          }}
        >
          {loading
            ? "Loading..."
            : mode === "login"
            ? "Sign in"
            : "Create account"}
        </Button>

        <Box
          sx={{
            display: { xs: "block", md: "none" },
            textAlign: "center",
            mt: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {mode === "login"
              ? "Don't have an account? "
              : "Already have an account? "}
            <Button
              component={RouterLink}
              to={alternateHref}
              variant="text"
              size="small"
              sx={{
                ...brandTextAccentSx,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              {mode === "login" ? "Sign up" : "Sign in"}
            </Button>
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default AuthCredentialsForm;
